// src/components/dashboard/AgentDashboard.jsx

import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, CircularProgress, Stack,
  Card, CardContent, Snackbar, Alert, Divider, Chip,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import PersonIcon from "@mui/icons-material/Person";
import SavingsIcon from "@mui/icons-material/Savings";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { useNavigate, useLocation } from "react-router-dom";
import DashboardTabs from "./DashBoardTabs";
import { useUserListings } from "../../hooks/useUserListings";
import SmartLocksControl from "./SmartLocksControl";
import DeviceManagement from "./DeviceManagement";
import ViewingRequests from "./ViewingRequest";
import ManageListings from "./ManageListings";
import MaintenanceVendorRequests from "./MaintenanceVendorRequests";
import useSubscriptionStatus from "../../hooks/useSubscriptionStatus";

import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../hooks/useAuth";

const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/cN26qW0ZZb141kkfYY";

const BACKEND_URL =
  (process.env.NODE_ENV === "development"
    ? "http://localhost:4000"
    : "https://ai-realtor-2ktt.onrender.com");

// ─── Google Calendar section component ───────────────────────────────────────

const GoogleCalendarSection = ({ uid }) => {
  const [gcalStatus, setGcalStatus] = useState(null); // null = loading
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectedAt, setConnectedAt] = useState(null);

  // Listen to Firestore for live connection status
  useEffect(() => {
    if (!uid) return;
    let active = true;
    const ref = doc(db, "users", uid, "integrations", "googleCalendar");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!active) return;
        if (snap.exists()) {
          const data = snap.data();
          setGcalStatus({ connected: true, email: data.email || null });
          // connectedAt is a Firestore Timestamp
          setConnectedAt(data.connectedAt?.toDate?.() || null);
        } else {
          setGcalStatus({ connected: false });
          setConnectedAt(null);
        }
      },
      (err) => {
        if (!active) return;
        console.error("[GCal UI] Firestore snapshot error:", err);
        setGcalStatus({ connected: false });
      }
    );
    return () => {
      active = false;
      unsub();
    };
  }, [uid]);

  const handleConnect = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/google-calendar/auth-url?uid=${uid}`);
      if (!response.ok) throw new Error("Failed to get auth URL");
      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error("[GCal UI] Connect error:", err);
      setError("Could not start Google Calendar connection. Please try again.");
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      `Disconnect ${gcalStatus?.email || "Google Calendar"}?\n\nDouble-booking protection will be disabled until you reconnect.`
    )) return;

    setActionLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/google-calendar/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      if (!response.ok) throw new Error("Disconnect failed");
      // onSnapshot fires automatically and updates UI
    } catch (err) {
      console.error("[GCal UI] Disconnect error:", err);
      setError("Could not disconnect. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (gcalStatus === null) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Checking Google Calendar…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: gcalStatus.connected ? "rgba(76, 175, 80, 0.35)" : "rgba(0,0,0,0.12)",
        backgroundColor: gcalStatus.connected ? "rgba(76, 175, 80, 0.05)" : "rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
      }}
    >
      {/* Header row */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <CalendarMonthIcon
          sx={{ fontSize: 20, color: gcalStatus.connected ? "#81c241" : "text.secondary" }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Google Calendar
        </Typography>
        {gcalStatus.connected && (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
            label="Connected"
            size="small"
            sx={{
              backgroundColor: "rgba(76,175,80,0.15)",
              color: "#2e7d32",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 22,
            }}
          />
        )}
      </Box>

      {/* Connected account info */}
      {gcalStatus.connected ? (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "rgba(76, 175, 80, 0.08)",
            border: "1px solid rgba(76, 175, 80, 0.15)",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Connected as
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#2e7d32", wordBreak: "break-all" }}
          >
            {gcalStatus.email || "Unknown account"}
          </Typography>
          {connectedAt && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>
              Since {connectedAt.toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric",
              })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Your calendar is actively blocking out-of-office time and preventing double bookings across all properties.
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Connect to automatically block your calendar when viewings are booked and prevent double-booking across properties.
        </Typography>
      )}

      {/* Error */}
      {error && (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {error}
        </Typography>
      )}

      {/* Action buttons */}
      {gcalStatus.connected ? (
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={actionLoading
              ? <CircularProgress size={14} color="error" />
              : <LinkOffIcon />
            }
            onClick={handleDisconnect}
            disabled={actionLoading}
            sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.8rem" }}
          >
            {actionLoading ? "Disconnecting…" : "Disconnect"}
          </Button>
          {/* Re-connect with a different account */}
          <Button
            variant="text"
            size="small"
            onClick={handleConnect}
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              color: "text.secondary",
              "&:hover": { color: "#81c241" },
            }}
          >
            Switch account
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          size="small"
          startIcon={
            actionLoading
              ? <CircularProgress size={14} sx={{ color: "white" }} />
              : <CalendarMonthIcon />
          }
          onClick={handleConnect}
          disabled={actionLoading}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontSize: "0.8rem",
            backgroundColor: "#81c241",
            "&:hover": { backgroundColor: "#6aaa32" },
          }}
        >
          {actionLoading ? "Redirecting…" : "Connect Google Calendar"}
        </Button>
      )}
    </Box>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const AgentDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [successAlert, setSuccessAlert] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { listings, loading, error, deleteListing, refetch } = useUserListings();
  const { loading: subLoading } = useSubscriptionStatus();

  // GCal OAuth redirect result
  const [gcalAlert, setGcalAlert] = useState(null); // { severity, message }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gcal = params.get("gcal");
    const tab = params.get("tab");

    if (tab === "3") setTabValue(3);

    if (gcal === "success") {
      setGcalAlert({ severity: "success", message: "Google Calendar connected successfully! 🎉" });
      // Clean URL
      navigate("/agent-dashboard?tab=3", { replace: true });
    } else if (gcal === "error") {
      setGcalAlert({ severity: "error", message: "Google Calendar connection failed. Please try again." });
      navigate("/agent-dashboard?tab=3", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (location.state?.propertyAdded) {
      setSuccessAlert(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const { currentUser, logout } = useAuth();
  const uid = currentUser?.uid || null;

  const [rawRole, setRawRole] = useState(null);
  const [offsetData, setOffsetData] = useState({ activeTenantCount: 0, billingOffset: 0 });

  useEffect(() => {
    if (!uid) {
      setRawRole(null);
      setOffsetData({ activeTenantCount: 0, billingOffset: 0 });
      return;
    }
    let active = true;
    const ref = doc(db, "users", uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!active) return;
      const data = snap.data();
      setRawRole(data?.userType ?? null);
      if (data?.tenantBilling) setOffsetData(data.tenantBilling);
    });
    return () => {
      active = false;
      unsub();
    };
  }, [uid]);

  const [accessCodeData, setAccessCodeData] = useState(null);

  const handleTabChange = (_, newValue) => setTabValue(newValue);
  const handleGenerateAccessCode = (data) => {
    setAccessCodeData(data);
    setTabValue(2);
  };

  const handleCreateListing = () => navigate("/upload-property");
  const handleEditListing = (id) => navigate(`/edit-listing/${id}`);
  const handleViewListing = (id) => navigate(`/listing/${id}`);
  const handleDeleteListing = async (id) => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await deleteListing(id);
      } catch (err) {
        console.error("Failed to delete listing", err);
      }
    }
  };
  const handleEditProfile = () => navigate("/edit-profile", { state: { userType: "agent" } });
  const handleCalendarAccess = () => navigate("/calendar");
  const handleManageSubscription = () =>
    window.open(STRIPE_PORTAL_URL, "_blank", "noopener,noreferrer");

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/login");
    }
  };

  // Note: subscription is no longer required for dashboard access.
  // Smart lock connection/setup/config actions are guarded at their entry points.
  if (subLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardTabs
      tabValue={tabValue}
      onTabChange={handleTabChange}
      onCalendarAccess={handleCalendarAccess}
      onLogout={handleLogout}
      userType={rawRole}
    >
      {tabValue === 0 && (
        <ManageListings
          listings={listings}
          loading={loading}
          error={error}
          handleCreateListing={handleCreateListing}
          handleEditListing={handleEditListing}
          handleDeleteListing={handleDeleteListing}
          handleViewListing={handleViewListing}
          onTransfer={refetch}
        />
      )}
      {tabValue === 1 && (
        <ViewingRequests uid={uid} onGenerateAccessCode={handleGenerateAccessCode} />
      )}
      {tabValue === 2 && (
        <SmartLocksControl agentId={uid} accessCodeData={accessCodeData} />
      )}

      {/* ── Tab 3: Account Settings (R4 additions below) ── */}
      {tabValue === 3 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={{ xs: 2, sm: 3 }}
          px={{ xs: 1, sm: 0 }}
        >
          <Card
            elevation={2}
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              width: { xs: "100%", sm: "90%", md: 380 },
              maxWidth: 500,
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  fontWeight: 600,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                }}
              >
                Account Settings
              </Typography>

              {/* Tenant Contribution */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                  borderRadius: 3,
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SavingsIcon sx={{ color: "#81c241", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#81c241" }}>
                    Tenant Contribution
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    {offsetData.activeTenantCount} Active Tenants
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#81c241" }}>
                    -${offsetData.billingOffset.toFixed(2)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mt: 0.5, fontSize: "0.7rem" }}
                >
                  Monthly billing offset applied to your account
                </Typography>
              </Box>

              {/* ── Google Calendar integration (R4) ── */}
              <GoogleCalendarSection uid={uid} />

              <Divider sx={{ my: 2.5 }} />

              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => navigate("/tenant-login")}
                  sx={{ textTransform: "none", borderRadius: 3, py: 1, fontSize: "0.9rem" }}
                  fullWidth
                >
                  Switch to Tenant View
                </Button>

                <Divider />

                <Button
                  variant="contained"
                  startIcon={<PersonIcon />}
                  color="primary"
                  onClick={handleEditProfile}
                  sx={{
                    textTransform: "none",
                    borderRadius: 3,
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                  fullWidth
                >
                  Edit Profile
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<LaunchIcon />}
                  onClick={handleManageSubscription}
                  sx={{
                    textTransform: "none",
                    borderRadius: 3,
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                  fullWidth
                >
                  Manage Subscription
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {tabValue === 4 && <DeviceManagement />}
      {tabValue === 5 && <MaintenanceVendorRequests />}

      {/* Property upload success */}
      <Snackbar
        open={successAlert}
        autoHideDuration={5000}
        onClose={() => setSuccessAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessAlert(false)} severity="success" variant="filled" sx={{ width: "100%", fontWeight: 600, boxShadow: 3 }}>
          Your property was uploaded successfully! 🎉
        </Alert>
      </Snackbar>

      {/* GCal OAuth result */}
      <Snackbar
        open={!!gcalAlert}
        autoHideDuration={6000}
        onClose={() => setGcalAlert(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setGcalAlert(null)}
          severity={gcalAlert?.severity || "info"}
          variant="filled"
          sx={{ width: "100%", fontWeight: 600, boxShadow: 3 }}
        >
          {gcalAlert?.message}
        </Alert>
      </Snackbar>
    </DashboardTabs>
  );
};

export default AgentDashboard;
